import React, { Suspense } from 'react';
import { useGlobal } from 'reactn';
import { FolderNav, Theme, FeedsAllFeeds, FeedsNewMessageModal, Badge } from '@orijinworks/frontend-commons';
import { Box, CircularProgress, useTheme } from '@material-ui/core';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useFeedsManager, useAttorneyMessagingHook } from './hooks';
import { useDocumentTitleHook } from '../../shared/hooks';
import FeedsErrorBoundary from './components/FeedsErrorBoundary';
import { useFeedConfigureHook } from './useFeedConfigureHook';
import { FeedMembersAndReceiptsInfoModal } from '../../shared/components';
import { AuditLogButton, FeedsApplicationStyles } from './Feeds.style';
import HistoryIcon from '@mui/icons-material/History';
import { Link } from 'react-router-dom';
import AttorneyModal from './components/AttorneyModal';

// Lazy load components for better performance
const Announcements = React.lazy(() => import('./components/Announcements/Announcements'));
const Messages = React.lazy(() => import('./components/Messages/Messages'));

/**
 * Feeds Page - Optimized with Unified Hook Architecture
 *
 * Uses useFeedsManager to prevent unnecessary re-renders:
 * - All Feeds: Feed listing, pagination, socket events for new messages
 * - Direct Messages: Conversation management, messaging, thread handling
 * - Clean data flow and business logic separation
 * - Hooks access user data directly from localStorage
 * - No stale closure issues or reference problems
 * - Implements React.memo for performance optimization
 * - Uses lazy loading for better initial load performance
 *
 * @returns {JSX.Element}
 */
const Feeds = React.memo(() => {
  // Set document title
  useDocumentTitleHook('feed');
  const [global] = useGlobal();
  const feedsManager = useFeedsManager();
  const theme = useTheme();
  const {
    showNewMessageModal,
    modalContext,
    editSettings,
    tabsConfig,
    handleNewMessage,
    handleCloseModal,
    handleOpenEditModal } = useFeedConfigureHook({
      unreadCount: feedsManager?.totalUnreadCount,
      unreadAnnouncementsCount: feedsManager?.unreadCounts?.unreadAnnouncementsCount,
      unreadMessagesCount: feedsManager?.unreadCounts?.unreadMessagesCount,
      unreadChannelsCount: feedsManager?.unreadCounts?.unreadChannelsCount,
    })

  // Attorney modal state and handlers
  const {
    canShowAttorneyLink,
    showAttorneyModal,
    handleOpenAttorneyModal,
    handleCloseAttorneyModal,
  } = useAttorneyMessagingHook(handleCloseModal);

  // Create label components with badges
  const createLabelWithCount = (
    value,
    count,
    testId,
  ) => (
    <span {...(testId && { 'data-testid': testId })}>
      {value}
      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
      {count > 0 && (
        <Badge
          themeColor="red"
          variant="standard"
          badgeContent={count > 99 ? '99+' : count}
          style={{ padding: 0, fontSize: 12 }}
        />
      )}
    </span>
  );

  // Build tabs from config
  const tabs = tabsConfig
    .filter((tabConfig) => !tabConfig.hide)
    .map((tabConfig) => ({
      label: tabConfig.hasUnreadCount
        ? createLabelWithCount(
          tabConfig.value,
          tabConfig.unreadCount,
          tabConfig.key === 'all' ? 'all-feeds-tab-label' : undefined,
        )
        : tabConfig.value,
      value: tabConfig.value,
      Icon: tabConfig.Icon,
    }));
  if (showNewMessageModal) {
    return (
      <FeedsNewMessageModal
        hasLearner={feedsManager.dm.feedDetails?.hasLearner}
        manageFeedPermissions={feedsManager.manageFeedPermissions}
        modalContext={modalContext}
        onClose={handleCloseModal}
        memberOptions={feedsManager.manageFeedProps.searchedMembers}
        onMemberOptionsChange={feedsManager.manageFeedProps.onSearchChangeHandler}
        initialConversationSettings={{
          learnerCanSendMessages: true,
          limitLearnerMessages: true,
          numberOfMessages: feedsManager.manageFeedProps.DEFAULT_NUMBER_OF_MESSAGES_ALLOWED,
          shutDownConversation: false,
        }}
        onCreateConversation={feedsManager.manageFeedProps.onCreateHandler}
        onCreateAnnouncement={feedsManager.manageFeedProps.onCreateAnnouncement}
        maximumMembersAllowed={modalContext === 'message' ? 1 : 10}
        showSettings={true}
        isMemberListLoading={feedsManager.manageFeedProps.isMemberSearchLoading}
        isCreateLoading={feedsManager.manageFeedProps.isManageFeedLoading}
        {...(canShowAttorneyLink && { onOpenAttorneyModal: handleOpenAttorneyModal })}
        {...(modalContext === 'edit' && {
          onSaveChanges: feedsManager.manageFeedProps.onSaveChanges,
          editSettings: editSettings,
        })}
      />
    );
  }

  if (showAttorneyModal) {
    return (
      <AttorneyModal
        onClose={handleCloseAttorneyModal}
        onCreateConversation={feedsManager.manageFeedProps.onCreateHandler}
        maximumMembersAllowed={10}
        isCreateLoading={feedsManager.manageFeedProps.isManageFeedLoading}
        initialConversationSettings={{
          learnerCanSendMessages: true,
          limitLearnerMessages: true,
          numberOfMessages: feedsManager.manageFeedProps.DEFAULT_NUMBER_OF_MESSAGES_ALLOWED,
          shutDownConversation: false,
        }}
      />
    );
  }
  return (
    <Theme>
      <FeedsErrorBoundary>
        <Box
          role="main"
          aria-label="Feeds application"
          position="relative"
          sx={FeedsApplicationStyles}
        >
          {(global.isSuperAdmin || global.isFacilityAdmin) && (
            <AuditLogButton
              variant="outlined" color="primary"
              LinkComponent={Link}
              to="/feeds/audit"
            >
              <HistoryIcon />
              Audit Log
            </AuditLogButton>
          )}
          <FolderNav
            scrollButtons={false}
            tabFontColor={'var(--blue-main)'}
            tabs={tabs}
            defaultActiveTab={feedsManager.tabNavigation.activeTab}
            onTabChange={feedsManager.onTabChangeHandler}
            role="tablist"
            aria-label="Feed navigation tabs"
          >
            {feedsManager.tabNavigation.activeTab === 'All' && (
              <div role="tabpanel" aria-labelledby="all-tab" id="all-panel">
                <FeedsAllFeeds
                  {...feedsManager.allFeeds}
                />
              </div>
            )}
            {feedsManager.tabNavigation.activeTab === 'Messages' && (
              <Suspense fallback={<Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>}>
                <Messages
                  {...feedsManager.dm}
                  onNewMessageHandler={() => handleNewMessage('message')}
                  onFeedSettingEditHandler={feedsManager.manageFeedProps.onFeedSettingEditHandler}
                  onEditSettingsHandler={handleOpenEditModal}
                  messageViewRef={feedsManager.messageViewRef}
                />
              </Suspense>
            )}
            {feedsManager.tabNavigation.activeTab === 'Announcements' && (
              <div role="tabpanel" aria-labelledby="announcements-tab" id="announcements-panel">
                <Suspense fallback={<Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>}>
                  <Announcements
                    {...feedsManager.announcements}
                    onNewAnnouncementHandler={() => handleNewMessage('announcement')}
                    onAnnouncementDeleteHandler={feedsManager.announcements.onAnnouncementDeleteHandler}
                  />
                </Suspense>
              </div>
            )}
          </FolderNav>
        </Box>
        {/* Read Receipts Modal - rendered at top level to avoid prop drilling */}
        {feedsManager?.dm?.readReceiptsModal && feedsManager?.dm?.readReceiptsModal.open && (
          <FeedMembersAndReceiptsInfoModal
            open={feedsManager.dm.readReceiptsModal.open}
            onClose={feedsManager.dm.readReceiptsModal.onClose}
            heading={feedsManager.dm.readReceiptsModal.heading}
            badgeLabel={feedsManager.dm.readReceiptsModal.badgeLabel}
            members={feedsManager.dm.readReceiptsModal.members}
            headingIcon={
              <DoneAllIcon
                fontSize="small"
                sx={{ color: theme?.palette?.colors?.blueMain }}
              />
            }
            showFooter={false}
            styles={{
              mainWrapperStyles: {
                maxHeight: '450px',
                overflowY: 'auto',
                height: 'auto !important',
                borderBottom: 'none !important'
              }
            }}
          />
        )}
      </FeedsErrorBoundary>
    </Theme>
  );
});

export default Feeds;